import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BASE_URL,
  fetchSampleRequestMonths,
  proceedsamplerequestorder90days,
  getSample90DaysMonthsReport,
} from '../../Service/ApiService';
import { showAlert } from 'utils/ErrorMessage';
import { showSuccessAlert } from 'utils/SuccessMessage';

const SampleSelection90daysController = () => {
  const REPORTS_PAYLOAD_KEY = "samplePlanningReportsPayload90Days";
  const navigate = useNavigate();
  const location = useLocation();
  const incomingData = location.state?.barcodeData || [];
  const incomingReportsPayload = location.state?.reportsPayload || null;
  // console.log('incomingData===>', incomingData);
  const [barcodeData, setBarcodeData] = useState([]);
  // console.log('barcodeData===>', barcodeData);
  const [monthList, setMonthList] = useState([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [loadingInlineReports, setLoadingInlineReports] = useState(false);
  const [loadingReportDialog, setLoadingReportDialog] = useState(false);
  const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
  const [remark, setRemark] = useState('');
  const [remarkError, setRemarkError] = useState("");
  const [openReportDialog, setOpenReportDialog] = useState(false);

  const [reportData, setReportData] = useState([]);
  const [financialYears, setFinancialYears] = useState({
    currentFY: "",
    previousFY: ""
  });
  const [itemReportMap, setItemReportMap] = useState({});
  const loadedReportKeysRef = useRef(new Set());

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const confvalue = JSON.parse(localStorage.getItem("confvalue")) || {};

  //const barcodeArraySchema = Yup.array().of(barcodeItemSchema);

  useEffect(() => {
    fetchMonths();
  }, []);

  useEffect(() => {
    let seeded = null;

    if (incomingReportsPayload && typeof incomingReportsPayload === "object") {
      seeded = incomingReportsPayload;
      localStorage.setItem(REPORTS_PAYLOAD_KEY, JSON.stringify(seeded));
    } else {
      try {
        const cached = localStorage.getItem(REPORTS_PAYLOAD_KEY);
        seeded = cached ? JSON.parse(cached) : null;
      } catch (e) {
        seeded = null;
      }
    }

    if (seeded && typeof seeded === "object") {
      setItemReportMap(seeded);
    }
  }, [incomingReportsPayload]);

  // ✅ When both incomingData & monthList are available, initialize
  useEffect(() => {
    if (incomingData.length > 0 && monthList.length > 0) {
      initializeBarcodeData(monthList);
    }
  }, [incomingData, monthList]);


  // ✅ Fetch 3 dynamic months from backend
  const fetchMonths = async () => {
    try {
      setLoadingMonths(true);
      const res = await fetchSampleRequestMonths();
      // console.log("✅ Month API Response:", res);
      if (res.success && res.data) {
        setMonthList(res.data);
      } else {
        console.warn("No months received from API");
      }
    } catch (err) {
      console.error("Error fetching months:", err);
    } finally {
      setLoadingMonths(false);
    }
  };

  const buildMonthQuantitiesFromReport = (months, reportRows = []) => {
    const monthQuantities = {};

    months.forEach((m) => {
      const key = `${m.month}_${m.year}`;
      const reportMatch = (reportRows || []).find(
        (row) =>
          normalizeMonth(row?.month) === normalizeMonth(m.month) &&
          String(row?.year || "") === String(m.year || "")
      );

      monthQuantities[key] =
        reportMatch && reportMatch.orderQty !== null && reportMatch.orderQty !== undefined
          ? String(reportMatch.orderQty)
          : "";
    });

    return monthQuantities;
  };

  // ✅ Initialize barcode data with month keys and 0 quantities
  const initializeBarcodeData = (months) => {
    const initialized = (incomingData || []).map((item, idx) => {
      const reportKey = getItemKey(item);
      const reportRows = itemReportMap?.[reportKey]?.rows || [];
      const monthQuantities = buildMonthQuantitiesFromReport(months, reportRows);
      return {
        ...item,
        id: idx + 1,
        months: monthQuantities,
        errors: {},
        sampleType: item.sampleType || null,
      };
    });

    setBarcodeData(initialized);
    // console.log("✅ Initialized barcodeData:", initialized);
  };

  // ✅ Update month quantity dynamically
  useEffect(() => {
    if (!monthList.length || !barcodeData.length) return;

    setBarcodeData((prev) =>
      prev.map((item) => {
        const reportKey = getItemKey(item);
        const reportRows = itemReportMap?.[reportKey]?.rows || [];

        if (!reportRows.length) return item;

        const reportMonthValues = buildMonthQuantitiesFromReport(monthList, reportRows);
        const mergedMonths = { ...(item.months || {}) };

        Object.entries(reportMonthValues).forEach(([monthKey, orderQty]) => {
          if ((mergedMonths[monthKey] === "" || mergedMonths[monthKey] === null || mergedMonths[monthKey] === undefined) && orderQty !== "") {
            mergedMonths[monthKey] = orderQty;
          }
        });

        return {
          ...item,
          months: mergedMonths
        };
      })
    );
  }, [itemReportMap, monthList]);

  const updateQuantity = (itemId, monthKey, value) => {
    setBarcodeData((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, months: { ...item.months, [monthKey]: value } }
          : item
      )
    );
  };

  // ✅ Simple quantity validation
  const validateQuantities = () => {
    for (const item of barcodeData) {
      const months = Object.entries(item.months || {});
      for (const [monthKey, qty] of months) {
        if (qty === null || qty === undefined || qty === "" || Number(qty) <= 0) {
          showAlert(`Please enter valid quantity for ${item.materialDesc} (${monthKey.replace("_", " ")})`);
          return false;
        }
      }
    }
    return true;
  };

  const submitSampleRequest = async () => {
    try {
      if (!barcodeData || barcodeData.length === 0) {
        showAlert("No barcode data found!");
        return;
      }

      // ✅ Validate quantities
      if (!validateQuantities()) return;
      setOpenRemarkDialog(true);

    } catch (err) {
      console.error("❌ submitSampleRequest Error:", err);
      showAlert("Something went wrong while validating!");
    }
  };


  const submitRemark = async () => {
    try {
      if (!remark.trim()) {
        setRemarkError("Please enter a remark");
        return;
      }

      setRemarkError("");
      const userCode = user?.userCode;
      const conferenceId = confvalue?.cID || "";

      // ✅ Build 90-day details array
      const SampleOrderDetails90Day = barcodeData.map((item) => {
        const months = Object.entries(item.months || {});
        const monthDetails = {};

        months.forEach(([key, qty], index) => {
          const [month, year] = key.split("_");
          const i = index + 1;
          monthDetails[`month${i}`] = month;
          monthDetails[`month${i}Year`] = year;
          monthDetails[`month${i}Quantity`] = Number(qty) || 0;
        });

        return {
          materialID: item.materialID || "",
          materialCode: item.materialCode || "",
          mainGroup: item.mainGroup || "",
          materialDesc: item.materialDesc || "",
          barcode: item.barcode || "",
          materialVertical: item.materialVertical || "",
          ...monthDetails,
        };
      });

      // ✅ Build payload
      const payload = {
        userCode,
        conferenceId,
        remark,
        sampleOrderDetails90DayListArray: {
          SampleOrderDetails90Day,
        },
      };

      // console.log("🟢 Final Payload:", payload);

      // ✅ Call API
      const response = await proceedsamplerequestorder90days(payload);
      if (response.success === true) {
        setOpenRemarkDialog(false);
        //console.log("🟢respone success hiiii");
        showSuccessAlert("Sample Request Submitted Successfully!", "Success");

        downloadFile(`${response.samReqDocNo}.pdf`);

        setTimeout(() => {
          localStorage.setItem(REPORTS_PAYLOAD_KEY, JSON.stringify(itemReportMap || {}));
          navigate("/dashboard-sales-planning", {
            state: {
              reportsPayload: itemReportMap || {}
            }
          });
        }, 500);
      } else {
        setOpenRemarkDialog(false);
        showAlert(response.message || "Sample request not submitted!");
      }
    } catch (err) {
      setOpenRemarkDialog(false);
      showAlert("Sample request not submitted!");
    }
  };

  const downloadFile = (fileName) => {
    if (!BASE_URL || !fileName) return;

    const fileUrl = `${BASE_URL}uploads/sample_request_90Day/${fileName}`;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName; // suggested filename
    link.target = "_blank";   // optional but safe
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // ✅ Fetch Dynamic Report Data + Open Dialog
  const getItemKey = (item = {}) =>
    `${item.materialCode || ""}|${item.materialDesc || ""}|${item.mainGroup || ""}|${item.barcode || ""}`;

  const normalizeMonth = (val = "") => String(val).trim().toLowerCase().slice(0, 3);
  const MONTH_ORDER = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const MONTH_LABELS = {
    jan: "Jan",
    feb: "Feb",
    mar: "Mar",
    apr: "Apr",
    may: "May",
    jun: "Jun",
    jul: "Jul",
    aug: "Aug",
    sep: "Sep",
    oct: "Oct",
    nov: "Nov",
    dec: "Dec"
  };
  const getShiftedMonthToken = (monthName, shiftBy = 0) => {
    const token = normalizeMonth(monthName);
    const currentIdx = MONTH_ORDER.indexOf(token);
    if (currentIdx === -1) return token;
    const shiftedIdx = (currentIdx + shiftBy + 12) % 12;
    return MONTH_ORDER[shiftedIdx];
  };

  const buildEmptyReportEntry = () => ({
    rows: [],
    financialYears: { currentFY: '', previousFY: '' }
  });

  const getResponseDataMap = (res) => {
    const data = res?.data;
    if (data && !Array.isArray(data) && typeof data === 'object') {
      return data;
    }

    if (res?.reports && typeof res.reports === 'object') {
      return res.reports;
    }

    return {};
  };

  const normalizeBatchReportResponse = (items, res) => {
    const responseMap = getResponseDataMap(res);
    const commonFinancialYears = res?.financialYears || { currentFY: '', previousFY: '' };

    return items.reduce((acc, item) => {
      const key = getItemKey(item);
      const rawEntry = responseMap[key] ?? responseMap[item?.barcode] ?? responseMap[item?.materialCode];
      const rows = Array.isArray(rawEntry)
        ? rawEntry
        : Array.isArray(rawEntry?.rows)
          ? rawEntry.rows
          : [];
      const financialYears = rawEntry?.financialYears || commonFinancialYears;

      acc[key] = {
        rows,
        financialYears
      };

      return acc;
    }, {});
  };

  const fetchInlineReportsBatch = async (items = []) => {
    const userCode = user?.userCode || '';
    if (!userCode || !items.length) return;

    const validItems = items.filter((item) => {
      const materialCode = item?.materialCode || '';
      const materialDesc = item?.materialDesc || '';
      const mainGroup = item?.mainGroup || '';
      const barcode = item?.barcode || '';
      return materialCode && materialDesc && mainGroup && barcode;
    });

    const pendingItems = validItems.filter((item) => {
      const key = getItemKey(item);
      return !loadedReportKeysRef.current.has(key);
    });

    if (!pendingItems.length) return;

    pendingItems.forEach((item) => {
      loadedReportKeysRef.current.add(getItemKey(item));
    });

    setLoadingInlineReports(true);
    try {
      const res = await getSample90DaysMonthsReport(userCode, pendingItems);
      const normalizedMap = normalizeBatchReportResponse(pendingItems, res);

      setItemReportMap((prev) => ({
        ...prev,
        ...normalizedMap
      }));
    } catch (err) {
      const fallbackMap = pendingItems.reduce((acc, item) => {
        acc[getItemKey(item)] = buildEmptyReportEntry();
        return acc;
      }, {});

      setItemReportMap((prev) => ({
        ...prev,
        ...fallbackMap
      }));
    } finally {
      setLoadingInlineReports(false);
    }
  };

  useEffect(() => {
    if (!barcodeData.length) return;
    fetchInlineReportsBatch(barcodeData);
  }, [barcodeData]);

  const getMonthYearWiseData = (item, monthName) => {
    const key = getItemKey(item);
    const report = itemReportMap[key];
    if (!report) {
      return {
        currentFY: financialYears.currentFY || "Current FY",
        currentQty: "-",
        previousFY: financialYears.previousFY || "Previous FY",
        previousQty: "-"
      };
    }

    const rows = Array.isArray(report.rows) ? report.rows : [];
    const currentFY = report.financialYears?.currentFY || "Current FY";
    const previousFY = report.financialYears?.previousFY || "Previous FY";
    const monthToken = normalizeMonth(monthName);
    const previousMonthToken = getShiftedMonthToken(monthName, -3);

    const currentMatch = rows.find(
      (r) =>
        normalizeMonth(r?.month) === previousMonthToken &&
        String(r?.financialYear || "") === String(currentFY)
    );

    const previousMatch = rows.find(
      (r) =>
        normalizeMonth(r?.month) === monthToken &&
        String(r?.financialYear || "") === String(report.financialYears?.previousFY || "")
    );

    return {
      currentFY,
      currentQty: currentMatch?.quantity ?? "-",
      currentFYMonth: MONTH_LABELS[previousMonthToken] || previousMonthToken,
      previousFY,
      previousQty: previousMatch?.quantity ?? "-",
      previousFYMonth: MONTH_LABELS[monthToken] || monthName
    };
  };

  const onClickDetails = async (item) => {
    try {
      const key = getItemKey(item);
      const cached = itemReportMap[key];
      if (cached) {
        setReportData(cached.rows || []);
        setFinancialYears(cached.financialYears || { currentFY: '', previousFY: '' });
        setOpenReportDialog(true);
        return;
      }

      const userCode = user?.userCode || '';
      const materialCode = item.materialCode || '';
      const materialDesc = item.materialDesc || '';
      const mainGroup = item.mainGroup || '';
      const barcode = item.barcode || '';

      if (!userCode || !materialCode || !materialDesc || !mainGroup || !barcode) {
        showAlert('Missing required parameters for report!');
        return;
      }

      setLoadingReportDialog(true);
      setOpenReportDialog(true);
      const res = await getSample90DaysMonthsReport(userCode, [
        {
          materialCode,
          materialDesc,
          mainGroup,
          barcode
        }
      ]);
      const normalizedMap = normalizeBatchReportResponse([item], res);
      const normalizedEntry = normalizedMap[key] || buildEmptyReportEntry();
      
      if (res.success) {
        setItemReportMap((prev) => ({
          ...prev,
          [key]: normalizedEntry
        }));
        setReportData(normalizedEntry.rows);
        setFinancialYears(normalizedEntry.financialYears);
      } else {
        setOpenReportDialog(false);
        showAlert(res.message || 'No report data found!');
      }
    } catch (err) {
      setOpenReportDialog(false);
      console.error('Error fetching 90 days report:', err);
      showAlert('Failed to load report data!');
    } finally {
      setLoadingReportDialog(false);
    }
  };

  return {
    barcodeData,
    submitSampleRequest,
    openRemarkDialog,
    setOpenRemarkDialog,
    remark,
    setRemark,
    remarkError,
    submitRemark,
    monthList,
    updateQuantity,
    onClickDetails,
    getMonthYearWiseData,
    openReportDialog,
    setOpenReportDialog,
    reportData,
    financialYears,
    loadingInlineReports,
    loadingReportDialog
  };
};

export default SampleSelection90daysController;
